import { useState, useMemo } from 'react'
import type { CSSProperties } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useSettings } from '../hooks/useSettings'
import { usePrinters } from '../hooks/usePrinters'
import { useFilamentSpools } from '../hooks/useFilamentSpools'
import { useProducts, useCreateProduct, useUpdateProduct } from '../hooks/useProducts'
import { useHardwarePresets } from '../hooks/useHardwarePresets'
import {
  useCalculatorScenarios,
  useSaveCalculatorScenario,
  useDeleteCalculatorScenario,
  fetchScenarioFull,
} from '../hooks/useCalculatorScenarios'
import {
  computePlateScenario,
  computePrintCosts,
  filamentCostForUsage,
  printerCapitalCostPerHour,
  printerElectricalCostPerHour,
  type PlateScenarioResult,
} from '../lib/pricing'
import { useCreatePrint } from '../hooks/usePrints'
import { useBuilds, useCreateBuild } from '../hooks/useBuilds'
import { useToast } from '../components/ToastContext'
import { PRINT_PURPOSES, PRINT_OUTCOMES } from '../hooks/usePrints'
import { Card, Chip, BottomSheet, Skeleton } from '../components/ui'
import { BackLink } from '../components/BackLink'
import type { Printer, FilamentSpool } from '../lib/database.types'

interface SlotDraft {
  spoolId: string
  modelGrams: string
  supportGrams: string
  purgeGrams: string
}
interface HardwareItemDraft {
  /** Local id for React keys; not persisted. */
  uid: string
  name: string
  /** Quantity used per unit of the product (not per plate). */
  quantity: string
  unitCost: string
}

interface RowDraft {
  id: string
  /** When this row was loaded from a saved scenario (or saved as one), this
   *  is the scenario's id. Subsequent 'Save scenario' calls update it instead
   *  of creating new copies. */
  scenarioId: string | null
  /** When this row was loaded from a product (or saved to one), this is the
   *  product's id. 'Save to product' defaults to overwriting this product;
   *  'Log as print' attaches the print to it. */
  productId: string | null
  name: string
  modelLink: string
  printTimeHrs: string
  printTimeMins: string
  unitsPerPlate: string
  hardwareItems: HardwareItemDraft[]
  handlingMinutesPerUnit: string
  salePricePerUnit: string
  momsCutPctOverride: string
  slots: [SlotDraft, SlotDraft, SlotDraft, SlotDraft]
  expanded: boolean
}

const rowPrintHours = (r: RowDraft): number =>
  (Number(r.printTimeHrs) || 0) + (Number(r.printTimeMins) || 0) / 60

const sumHardwareCostPerUnit = (items: HardwareItemDraft[]): number =>
  items.reduce(
    (sum, item) => sum + (Number(item.quantity) || 0) * (Number(item.unitCost) || 0),
    0,
  )
type SortKey = 'profitPerHour' | 'netProfitPerUnit' | 'marginPct' | 'name'

let _nextId = 1
const nextId = () => String(_nextId++)
const emptySlot = (): SlotDraft => ({
  spoolId: '',
  modelGrams: '',
  supportGrams: '',
  purgeGrams: '',
})

const slotTotalGrams = (s: SlotDraft): number =>
  (Number(s.modelGrams) || 0) +
  (Number(s.supportGrams) || 0) +
  (Number(s.purgeGrams) || 0)
const makeEmptyRow = (): RowDraft => ({
  id: nextId(),
  scenarioId: null,
  productId: null,
  name: '',
  modelLink: '',
  printTimeHrs: '',
  printTimeMins: '',
  unitsPerPlate: '',
  hardwareItems: [],
  handlingMinutesPerUnit: '0',
  salePricePerUnit: '',
  momsCutPctOverride: '',
  slots: [emptySlot(), emptySlot(), emptySlot(), emptySlot()],
  expanded: true,
})

let _nextHardwareUid = 1
const nextHardwareUid = () => `h${_nextHardwareUid++}`
const emptyHardware = (): HardwareItemDraft => ({
  uid: nextHardwareUid(),
  name: '',
  quantity: '1',
  unitCost: '',
})

const fmt2 = (n: number) =>
  n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const fmtPct = (n: number) => `${(n * 100).toFixed(0)}%`

export default function Calculator() {
  const { data: settings, isLoading: loadingSettings } = useSettings()
  const { data: printers, isLoading: loadingPrinters } = usePrinters()
  const { data: spools, isLoading: loadingSpools } = useFilamentSpools()
  const { data: products } = useProducts()
  const { data: hardwarePresets } = useHardwarePresets()
  const { data: scenarios } = useCalculatorScenarios()
  const { data: builds } = useBuilds()
  const saveScenarioMut = useSaveCalculatorScenario()
  const deleteScenarioMut = useDeleteCalculatorScenario()
  const createProduct = useCreateProduct()
  const updateProduct = useUpdateProduct()
  const createPrint = useCreatePrint()
  const createBuild = useCreateBuild()
  const { toast } = useToast()

  const [rows, setRows] = useState<RowDraft[]>([makeEmptyRow()])
  const [selectedPrinterId, setSelectedPrinterId] = useState<string>('')
  const [sortKey, setSortKey] = useState<SortKey>('profitPerHour')
  const [hideUnprofitable, setHideUnprofitable] = useState(false)

  const [saveSheetRowId, setSaveSheetRowId] = useState<string | null>(null)
  const [saveMode, setSaveMode] = useState<'pick' | 'create'>('pick')
  const [saveTargetProductId, setSaveTargetProductId] = useState<string>('')
  const [saveNewName, setSaveNewName] = useState('')
  const [saveError, setSaveError] = useState('')

  const [hardwareLibraryRowId, setHardwareLibraryRowId] = useState<string | null>(null)
  const [loadFromProductOpen, setLoadFromProductOpen] = useState(false)
  const [loadError, setLoadError] = useState('')

  const [scenarioPickerOpen, setScenarioPickerOpen] = useState(false)
  const [scenarioError, setScenarioError] = useState('')

  const [logSheetRowId, setLogSheetRowId] = useState<string | null>(null)
  const [logOutcome, setLogOutcome] = useState<(typeof PRINT_OUTCOMES)[number]>('success')
  const [logPurpose, setLogPurpose] = useState<(typeof PRINT_PURPOSES)[number]>('business-sale')
  const [logDate, setLogDate] = useState(() => new Date().toISOString().slice(0, 10))
  const [logUnits, setLogUnits] = useState('')
  const [logError, setLogError] = useState('')

  const isLoading = loadingSettings || loadingPrinters || loadingSpools

  const activePrinters = (printers ?? []).filter((p) => p.status === 'active')
  const activePrinter: Printer | undefined =
    activePrinters.find((p) => p.id === selectedPrinterId) ?? activePrinters[0]

  const spoolById = useMemo(() => {
    const m = new Map<string, FilamentSpool>()
    for (const s of spools ?? []) m.set(s.id, s)
    return m
  }, [spools])

  const computeRow = (row: RowDraft): PlateScenarioResult | null => {
    if (!activePrinter || !settings) return null
    const hours = rowPrintHours(row)
    const units = Number(row.unitsPerPlate)
    if (!hours || !units) return null

    const slots = row.slots.map((s) => {
      const total = slotTotalGrams(s)
      if (!s.spoolId || total <= 0) return { gramsUsed: 0, costPerKg: 0 }
      const spool = spoolById.get(s.spoolId)
      if (!spool || spool.purchase_price == null || spool.original_weight_grams <= 0) {
        return { gramsUsed: total, costPerKg: 0 }
      }
      return {
        gramsUsed: total,
        costPerKg: (Number(spool.purchase_price) / Number(spool.original_weight_grams)) * 1000,
      }
    })

    return computePlateScenario({
      printer: activePrinter,
      settings: {
        electricity_rate_per_kwh: settings.electricity_rate_per_kwh,
        labor_hourly_rate: settings.labor_hourly_rate,
        material_efficiency_factor: settings.material_efficiency_factor,
        default_moms_cut_pct: settings.default_moms_cut_pct,
      },
      printTimeHours: hours,
      unitsPerPlate: units,
      hardwareCostPerUnit: sumHardwareCostPerUnit(row.hardwareItems),
      handlingMinutesPerUnit: Number(row.handlingMinutesPerUnit) || 0,
      salePricePerUnit: Number(row.salePricePerUnit) || 0,
      momsCutPctOverride: row.momsCutPctOverride ? Number(row.momsCutPctOverride) : null,
      slots,
    })
  }

  const rowsWithResults = useMemo(
    () => rows.map((row) => ({ row, result: computeRow(row) })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, activePrinter, settings, spoolById],
  )

  const sortedRows = useMemo(() => {
    let filtered = rowsWithResults
    if (hideUnprofitable) {
      filtered = filtered.filter((r) => r.result == null || r.result.netProfitPerUnit >= 0)
    }
    return [...filtered].sort((a, b) => {
      if (sortKey === 'name') return (a.row.name || '').localeCompare(b.row.name || '')
      const av = a.result?.[sortKey] ?? -Infinity
      const bv = b.result?.[sortKey] ?? -Infinity
      return bv - av
    })
  }, [rowsWithResults, sortKey, hideUnprofitable])

  const updateRow = (id: string, patch: Partial<RowDraft>) =>
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)))
  const updateSlot = (rowId: string, slotIdx: number, patch: Partial<SlotDraft>) =>
    setRows((prev) =>
      prev.map((r) => {
        if (r.id !== rowId) return r
        const slots = r.slots.map((s, i) => (i === slotIdx ? { ...s, ...patch } : s)) as RowDraft['slots']
        return { ...r, slots }
      }),
    )
  const addHardwareItem = (rowId: string) =>
    setRows((prev) =>
      prev.map((r) =>
        r.id === rowId ? { ...r, hardwareItems: [...r.hardwareItems, emptyHardware()] } : r,
      ),
    )
  const updateHardwareItem = (rowId: string, uid: string, patch: Partial<HardwareItemDraft>) =>
    setRows((prev) =>
      prev.map((r) => {
        if (r.id !== rowId) return r
        return {
          ...r,
          hardwareItems: r.hardwareItems.map((h) => (h.uid === uid ? { ...h, ...patch } : h)),
        }
      }),
    )
  const removeHardwareItem = (rowId: string, uid: string) =>
    setRows((prev) =>
      prev.map((r) =>
        r.id === rowId ? { ...r, hardwareItems: r.hardwareItems.filter((h) => h.uid !== uid) } : r,
      ),
    )
  // Build the wire-format payload for a row → save_calculator_scenario.
  const rowToSavePayload = (row: RowDraft) => ({
    id: row.scenarioId ?? null,
    product_id: row.productId,
    printer_id: activePrinter?.id ?? null,
    name: row.name.trim() || '(unnamed design)',
    model_link: row.modelLink.trim() || null,
    print_time_hrs: Number(row.printTimeHrs) || 0,
    print_time_mins: Number(row.printTimeMins) || 0,
    units_per_plate: Number(row.unitsPerPlate) || 0,
    handling_minutes_per_unit: Number(row.handlingMinutesPerUnit) || 0,
    sale_price_per_unit: Number(row.salePricePerUnit) || 0,
    moms_cut_pct_override: row.momsCutPctOverride ? Number(row.momsCutPctOverride) : null,
    slots: row.slots.map((s, i) => ({
      slot_index: i,
      spool_id: s.spoolId || null,
      model_grams: Number(s.modelGrams) || 0,
      support_grams: Number(s.supportGrams) || 0,
      purge_grams: Number(s.purgeGrams) || 0,
    })),
    hardware: row.hardwareItems
      .filter((h) => h.name.trim())
      .map((h) => ({
        name: h.name.trim(),
        quantity: Number(h.quantity) || 1,
        unit_cost: Number(h.unitCost) || 0,
      })),
  })

  const saveRowAsScenario = async (rowId: string) => {
    setScenarioError('')
    const row = rows.find((r) => r.id === rowId)
    if (!row) return
    try {
      const scenarioId = await saveScenarioMut.mutateAsync(rowToSavePayload(row))
      // Remember the id so future Save updates this row instead of cloning.
      setRows((prev) => prev.map((r) => (r.id === rowId ? { ...r, scenarioId } : r)))
    } catch (e) {
      setScenarioError((e as Error).message)
    }
  }

  const loadScenarioIntoRow = async (scenarioId: string) => {
    setScenarioError('')
    try {
      const full = await fetchScenarioFull(scenarioId)
      const slotsByIdx = new Map(full.slots.map((s) => [s.slot_index, s]))
      const buildSlot = (idx: number): SlotDraft => {
        const s = slotsByIdx.get(idx)
        if (!s) return emptySlot()
        return {
          spoolId: s.spool_id ?? '',
          modelGrams: Number(s.model_grams) > 0 ? String(s.model_grams) : '',
          supportGrams: Number(s.support_grams) > 0 ? String(s.support_grams) : '',
          purgeGrams: Number(s.purge_grams) > 0 ? String(s.purge_grams) : '',
        }
      }
      const newRow: RowDraft = {
        id: nextId(),
        scenarioId: full.scenario.id,
        productId: full.scenario.product_id,
        name: full.scenario.name,
        modelLink: full.scenario.model_link ?? '',
        printTimeHrs: full.scenario.print_time_hrs > 0 ? String(full.scenario.print_time_hrs) : '',
        printTimeMins: full.scenario.print_time_mins > 0 ? String(full.scenario.print_time_mins) : '',
        unitsPerPlate: full.scenario.units_per_plate > 0 ? String(full.scenario.units_per_plate) : '',
        hardwareItems: full.hardware.map((h) => ({
          uid: nextHardwareUid(),
          name: h.name,
          quantity: String(h.quantity),
          unitCost: String(h.unit_cost),
        })),
        handlingMinutesPerUnit: String(full.scenario.handling_minutes_per_unit),
        salePricePerUnit: String(full.scenario.sale_price_per_unit),
        momsCutPctOverride:
          full.scenario.moms_cut_pct_override != null
            ? String(full.scenario.moms_cut_pct_override)
            : '',
        slots: [buildSlot(0), buildSlot(1), buildSlot(2), buildSlot(3)],
        expanded: true,
      }
      setRows((prev) => [...prev, newRow])
      setScenarioPickerOpen(false)
    } catch (e) {
      setScenarioError((e as Error).message)
    }
  }

  const deleteScenario = (scenarioId: string, name: string) => {
    if (!window.confirm(`Delete saved scenario "${name}"?`)) return
    deleteScenarioMut.mutate(scenarioId, {
      onSuccess: () => {
        // Disassociate any open rows that pointed to it.
        setRows((prev) => prev.map((r) => (r.scenarioId === scenarioId ? { ...r, scenarioId: null } : r)))
      },
    })
  }

  const openLogSheet = (rowId: string) => {
    const row = rows.find((r) => r.id === rowId)
    if (!row) return
    setLogSheetRowId(rowId)
    setLogOutcome('success')
    setLogPurpose('business-sale')
    setLogDate(new Date().toISOString().slice(0, 10))
    setLogUnits(row.unitsPerPlate || '')
    setLogError('')
  }

  const handleLogAsPrint = async () => {
    setLogError('')
    const row = rows.find((r) => r.id === logSheetRowId)
    if (!row || !row.productId || !activePrinter || !settings) {
      setLogError('Need an attached product, an active printer, and settings.')
      return
    }
    const unitsProduced = Number(logUnits) || 0
    if (unitsProduced <= 0) {
      setLogError('Units produced must be > 0.')
      return
    }
    const hours = rowPrintHours(row)
    if (hours <= 0) {
      setLogError('Print time must be > 0.')
      return
    }

    // Build the usages — one per slot that has a spool + grams.
    const usagesInput = row.slots
      .map((s) => ({
        slot: s,
        spool: s.spoolId ? spoolById.get(s.spoolId) : undefined,
        totalGrams: slotTotalGrams(s),
      }))
      .filter((x) => x.spool && x.totalGrams > 0)

    if (usagesInput.length === 0) {
      setLogError('Add filament to at least one slot before logging.')
      return
    }

    const usages = usagesInput.map(({ slot, spool, totalGrams }) => {
      const cost = filamentCostForUsage(
        totalGrams,
        { purchase_price: spool!.purchase_price, original_weight_grams: spool!.original_weight_grams },
        settings.material_efficiency_factor,
      )
      return {
        spool_id: slot.spoolId,
        grams_used: totalGrams,
        cost_recognized: cost,
      }
    })

    // Compute the print's total cost the same way PrintForm does — so
    // historical print records use the canonical formula.
    const laborMinutes = (Number(row.handlingMinutesPerUnit) || 0) * unitsProduced
    const breakdown = computePrintCosts({
      printer: activePrinter,
      settings,
      printTimeHours: hours,
      laborMinutes,
      usages: usagesInput.map(({ spool, totalGrams }) => ({
        grams_used: totalGrams,
        spool: {
          purchase_price: spool!.purchase_price,
          original_weight_grams: spool!.original_weight_grams,
        },
      })),
    })

    try {
      // Use an in-progress build for this product if one exists; otherwise
      // create a fresh completed build sized to this print's output.
      const openBuild = (builds ?? []).find(
        (b) => b.product_id === row.productId && b.status === 'in_progress',
      )
      let buildId: string
      if (openBuild) {
        buildId = openBuild.id
      } else {
        const created = await createBuild.mutateAsync({
          product_id: row.productId,
          units: unitsProduced,
          status: 'completed',
          completed_at: new Date().toISOString(),
        })
        buildId = created.id
      }

      await createPrint.mutateAsync({
        print: {
          printer_id: activePrinter.id,
          print_date: logDate,
          print_time_hours: hours,
          labor_minutes: laborMinutes,
          units_produced: unitsProduced,
          product_id: row.productId,
          build_id: buildId,
          outcome: logOutcome,
          purpose: logPurpose,
          total_cost: breakdown.totalCost,
          filament_cost_total: breakdown.filamentCost,
          machine_cost: breakdown.machineCost,
          labor_cost: breakdown.laborCost,
          electricity_cost: breakdown.electricityCost,
          description: row.name || null,
        },
        usages,
        buildIds: [buildId],
      })

      toast({
        tone: 'success',
        text: `Print logged · ${unitsProduced} ${row.name || 'units'} added to inventory`,
      })
      setLogSheetRowId(null)
    } catch (e) {
      setLogError((e as Error).message)
    }
  }

  const loadFromProduct = async (productId: string) => {
    setLoadError('')
    const product = products?.find((p) => p.id === productId)
    if (!product) {
      setLoadError('Product not found.')
      return
    }
    // Fetch this product's hardware items so we can pre-fill them.
    const { data: hwRows, error: hwErr } = await supabase
      .from('product_hardware_items')
      .select('name, quantity, unit_cost, sort_order')
      .eq('product_id', productId)
      .order('sort_order', { ascending: true })
    if (hwErr) {
      setLoadError(hwErr.message)
      return
    }
    const hardwareItems: HardwareItemDraft[] = (hwRows ?? []).map((h) => ({
      uid: nextHardwareUid(),
      name: h.name,
      quantity: String(h.quantity),
      unitCost: String(h.unit_cost),
    }))

    // Split typical_print_time_hours into integer hrs + mins.
    const totalHours = product.typical_print_time_hours ?? 0
    const hrs = Math.floor(totalHours)
    const mins = Math.round((totalHours - hrs) * 60)

    // Pre-fill slot 1's model grams with the product's typical filament total.
    // The user still has to pick the spool — we don't know which one historically
    // and price varies by spool.
    const slot1: SlotDraft = {
      spoolId: '',
      modelGrams: product.typical_filament_grams != null ? String(product.typical_filament_grams) : '',
      supportGrams: '',
      purgeGrams: '',
    }

    const newRow: RowDraft = {
      id: nextId(),
      scenarioId: null,
      productId: product.id,
      name: product.name,
      modelLink: product.model_source ?? '',
      printTimeHrs: totalHours > 0 ? String(hrs) : '',
      printTimeMins: totalHours > 0 && mins > 0 ? String(mins) : '',
      unitsPerPlate: '',
      hardwareItems,
      handlingMinutesPerUnit: String(product.typical_labor_minutes ?? 0),
      salePricePerUnit:
        product.default_unit_price != null ? String(product.default_unit_price) : '',
      momsCutPctOverride:
        product.moms_cut_pct_override != null ? String(product.moms_cut_pct_override) : '',
      slots: [slot1, emptySlot(), emptySlot(), emptySlot()],
      expanded: true,
    }
    setRows((prev) => [...prev, newRow])
    setLoadFromProductOpen(false)
  }

  const addHardwareFromPreset = (rowId: string, presetId: string) => {
    const preset = hardwarePresets?.find((p) => p.id === presetId)
    if (!preset) return
    setRows((prev) =>
      prev.map((r) =>
        r.id === rowId
          ? {
              ...r,
              hardwareItems: [
                ...r.hardwareItems,
                {
                  uid: nextHardwareUid(),
                  name: preset.name,
                  quantity: String(preset.default_quantity),
                  unitCost: String(preset.unit_cost),
                },
              ],
            }
          : r,
      ),
    )
    setHardwareLibraryRowId(null)
  }
  const addRow = () => setRows((prev) => [...prev, makeEmptyRow()])
  const removeRow = (id: string) => setRows((prev) => prev.filter((r) => r.id !== id))

  const openSaveSheet = (rowId: string) => {
    const row = rows.find((r) => r.id === rowId)
    setSaveSheetRowId(rowId)
    setSaveMode('pick')
    // If the row was loaded from a product, default the save target back to
    // it so 'Save' overwrites the same product instead of a random one.
    setSaveTargetProductId(row?.productId ?? products?.[0]?.id ?? '')
    setSaveNewName('')
    setSaveError('')
  }

  const handleSaveToProduct = async () => {
    setSaveError('')
    const row = rows.find((r) => r.id === saveSheetRowId)
    if (!row) return
    const salePrice = Number(row.salePricePerUnit) || 0
    const cutOverride = row.momsCutPctOverride ? Number(row.momsCutPctOverride) : null

    // Per-unit calibrations to write back so 'Load from product' replays
    // the same scenario and PrintForm pre-fills with these values.
    const units = Number(row.unitsPerPlate) || 0
    const plateHrs = rowPrintHours(row)
    const handlingMins = Number(row.handlingMinutesPerUnit) || 0
    const plateGrams = row.slots.reduce((s, slot) => s + slotTotalGrams(slot), 0)
    if (units < 0 || plateHrs < 0 || handlingMins < 0 || plateGrams < 0) {
      setSaveError('Negative values are not allowed.')
      return
    }
    const perUnitGrams = units > 0 ? plateGrams / units : null
    const perUnitHours = units > 0 ? plateHrs / units : null

    const modelLink = row.modelLink.trim() || null

    try {
      let targetProductId: string
      if (saveMode === 'pick') {
        if (!saveTargetProductId) {
          setSaveError('Pick a product.')
          return
        }
        await updateProduct.mutateAsync({
          id: saveTargetProductId,
          default_unit_price: salePrice,
          moms_cut_pct_override: cutOverride,
          typical_filament_grams: perUnitGrams,
          typical_print_time_hours: perUnitHours,
          typical_labor_minutes: handlingMins,
          ...(modelLink ? { model_source: modelLink } : {}),
        })
        targetProductId = saveTargetProductId
      } else {
        if (!saveNewName.trim()) {
          setSaveError('Enter a product name.')
          return
        }
        const created = await createProduct.mutateAsync({
          name: saveNewName.trim(),
          default_unit_price: salePrice,
          moms_cut_pct_override: cutOverride,
          typical_filament_grams: perUnitGrams,
          typical_print_time_hours: perUnitHours,
          typical_labor_minutes: handlingMins,
          ...(modelLink ? { model_source: modelLink } : {}),
        })
        targetProductId = created.id
      }

      // Sync hardware items: replace whatever's on the product with the
      // calculator row's current items. Mirrors what ProductForm does via
      // useReplaceProductHardware but inline since we can't use a hook here.
      await supabase.from('product_hardware_items').delete().eq('product_id', targetProductId)
      const hwRows = row.hardwareItems
        .filter((h) => h.name.trim())
        .map((h, i) => ({
          name: h.name.trim(),
          quantity: Number(h.quantity) || 1,
          unit_cost: Number(h.unitCost) || 0,
          sort_order: i,
        }))
      if (hwRows.length > 0) {
        const { data: { user } } = await supabase.auth.getUser()
        if (user) {
          await supabase.from('product_hardware_items').insert(
            hwRows.map((r) => ({ ...r, user_id: user.id, product_id: targetProductId })),
          )
        }
      }

      // Remember which product this row maps to — future saves and 'log as
      // print' go here without re-asking.
      setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, productId: targetProductId } : r)))
      setSaveSheetRowId(null)
    } catch (e) {
      setSaveError((e as Error).message)
    }
  }

  if (isLoading) {
    return (
      <div style={{ padding: 'var(--space-6)' }}>
        <BackLink fallback="/settings" />
        <h1 style={{ marginBottom: 'var(--space-4)' }}>Pricing calculator</h1>
        <Skeleton height={60} style={{ marginBottom: 'var(--space-3)' }} />
        <Skeleton height={120} />
      </div>
    )
  }

  if (!activePrinter) {
    return (
      <div style={{ padding: 'var(--space-6)' }}>
        <BackLink fallback="/settings" />
        <h1>Pricing calculator</h1>
        <p style={{ marginTop: 'var(--space-4)', color: 'var(--color-text-muted)' }}>
          Add an active printer first so the calculator knows your wear + electricity rates.
        </p>
        <Link
          to="/settings/printers/new"
          style={{
            display: 'inline-block',
            marginTop: 'var(--space-3)',
            padding: 'var(--space-3) var(--space-5)',
            background: 'var(--color-primary)',
            color: 'white',
            borderRadius: 'var(--radius-md)',
            fontWeight: 600,
          }}
        >
          Add a printer
        </Link>
      </div>
    )
  }

  const defaultMomsCut = settings?.default_moms_cut_pct ?? 25
  const buffer = settings?.material_efficiency_factor ?? 1
  const bufferPct = Math.round((buffer - 1) * 100)

  return (
    <div style={{ padding: 'var(--space-4)', maxWidth: 640 }}>
      <BackLink fallback="/settings" />
      <h1 style={{ marginBottom: 'var(--space-4)', fontSize: 'var(--font-size-xl)' }}>
        Pricing calculator
      </h1>

      <Card style={{ marginBottom: 'var(--space-4)' }}>
        <div
          style={{
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-text-muted)',
            display: 'flex',
            flexWrap: 'wrap',
            gap: 'var(--space-2)',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <span>
            Electricity ${settings?.electricity_rate_per_kwh}/kWh · Labor ${settings?.labor_hourly_rate}/hr · Buffer {bufferPct}% · Mom&apos;s cut {defaultMomsCut}%
          </span>
          <Link
            to="/settings"
            style={{
              color: 'var(--color-primary)',
              fontSize: 'var(--font-size-xs)',
              fontWeight: 600,
            }}
          >
            Change
          </Link>
        </div>
      </Card>

      {activePrinters.length > 1 && (
        <div style={{ marginBottom: 'var(--space-4)' }}>
          <label style={labelStyleBlock}>Printer</label>
          <select
            value={selectedPrinterId || activePrinter.id}
            onChange={(e) => setSelectedPrinterId(e.target.value)}
            style={selectStyle}
          >
            {activePrinters.map((p) => {
              const ratePerHr = printerCapitalCostPerHour(p) + printerElectricalCostPerHour(p, settings!)
              return (
                <option key={p.id} value={p.id}>
                  {p.name} — ${fmt2(ratePerHr)}/hr
                </option>
              )
            })}
          </select>
        </div>
      )}

      <div
        style={{
          display: 'flex',
          gap: 'var(--space-2)',
          flexWrap: 'wrap',
          marginBottom: 'var(--space-4)',
          alignItems: 'center',
        }}
      >
        <label style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
          Sort:
        </label>
        {(
          [
            { key: 'profitPerHour', label: '$/printer-hr' },
            { key: 'netProfitPerUnit', label: 'profit/unit' },
            { key: 'marginPct', label: 'margin %' },
            { key: 'name', label: 'name' },
          ] as { key: SortKey; label: string }[]
        ).map(({ key, label }) => (
          <button
            key={key}
            type="button"
            onClick={() => setSortKey(key)}
            style={pillStyle(sortKey === key, 'primary')}
          >
            {label}
          </button>
        ))}
        <button
          type="button"
          onClick={() => setHideUnprofitable((v) => !v)}
          style={{ ...pillStyle(hideUnprofitable, 'warning'), marginLeft: 'auto' }}
        >
          {hideUnprofitable ? 'Showing profitable only' : 'Hide unprofitable'}
        </button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
        {sortedRows.map(({ row, result }) => (
          <DesignRow
            key={row.id}
            row={row}
            result={result}
            spools={spools ?? []}
            defaultMomsCut={defaultMomsCut}
            hasPresets={(hardwarePresets?.length ?? 0) > 0}
            savingScenario={saveScenarioMut.isPending}
            onUpdate={(patch) => updateRow(row.id, patch)}
            onUpdateSlot={(idx, patch) => updateSlot(row.id, idx, patch)}
            onAddHardware={() => addHardwareItem(row.id)}
            onAddHardwareFromLibrary={() => setHardwareLibraryRowId(row.id)}
            onUpdateHardware={(uid, patch) => updateHardwareItem(row.id, uid, patch)}
            onRemoveHardware={(uid) => removeHardwareItem(row.id, uid)}
            onRemove={() => removeRow(row.id)}
            onSave={() => openSaveSheet(row.id)}
            onSaveScenario={() => saveRowAsScenario(row.id)}
            onLogAsPrint={() => openLogSheet(row.id)}
            canRemove={rows.length > 1}
          />
        ))}
      </div>

      <div style={{ display: 'flex', gap: 'var(--space-2)', marginTop: 'var(--space-4)' }}>
        <button
          type="button"
          onClick={addRow}
          style={{
            flex: 1,
            padding: 'var(--space-3) var(--space-4)',
            background: 'var(--color-primary)',
            color: 'white',
            borderRadius: 'var(--radius-md)',
            fontWeight: 600,
            fontSize: 'var(--font-size-sm)',
          }}
        >
          + Add design
        </button>
        <button
          type="button"
          onClick={() => {
            setLoadError('')
            setLoadFromProductOpen(true)
          }}
          disabled={!products || products.length === 0}
          style={{
            flex: 1,
            padding: 'var(--space-3) var(--space-4)',
            background: 'transparent',
            color: 'var(--color-primary)',
            border: '1px solid var(--color-primary)',
            borderRadius: 'var(--radius-md)',
            fontWeight: 600,
            fontSize: 'var(--font-size-sm)',
          }}
        >
          ↓ Load product
        </button>
        <button
          type="button"
          onClick={() => {
            setScenarioError('')
            setScenarioPickerOpen(true)
          }}
          disabled={!scenarios || scenarios.length === 0}
          style={{
            flex: 1,
            padding: 'var(--space-3) var(--space-4)',
            background: 'transparent',
            color: 'var(--color-primary)',
            border: '1px solid var(--color-primary)',
            borderRadius: 'var(--radius-md)',
            fontWeight: 600,
            fontSize: 'var(--font-size-sm)',
          }}
          title={
            scenarios && scenarios.length > 0
              ? `${scenarios.length} saved scenario${scenarios.length === 1 ? '' : 's'}`
              : 'No saved scenarios yet — save one from any design row'
          }
        >
          📂 Scenarios
        </button>
      </div>

      <BottomSheet
        open={logSheetRowId !== null}
        onClose={() => setLogSheetRowId(null)}
        title="Log as print"
      >
        <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)', marginBottom: 'var(--space-3)' }}>
          Confirm the details. A print transaction will be created and the
          units will be added to inventory.
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)', marginBottom: 'var(--space-3)' }}>
          <div>
            <label style={labelStyleBlock}>Outcome</label>
            <select
              value={logOutcome}
              onChange={(e) => setLogOutcome(e.target.value as (typeof PRINT_OUTCOMES)[number])}
              style={selectStyle}
            >
              {PRINT_OUTCOMES.map((o) => (
                <option key={o} value={o}>{o}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={labelStyleBlock}>Purpose</label>
            <select
              value={logPurpose}
              onChange={(e) => setLogPurpose(e.target.value as (typeof PRINT_PURPOSES)[number])}
              style={selectStyle}
            >
              {PRINT_PURPOSES.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)', marginBottom: 'var(--space-3)' }}>
          <div>
            <label style={labelStyleBlock}>Date</label>
            <input
              type="date"
              value={logDate}
              onChange={(e) => setLogDate(e.target.value)}
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyleBlock}>Units produced</label>
            <input
              type="text"
              inputMode="numeric"
              value={logUnits}
              onChange={(e) => setLogUnits(e.target.value)}
              style={inputStyle}
              placeholder="e.g., 12"
            />
          </div>
        </div>

        {logError && (
          <p style={{ color: 'var(--color-danger)', fontSize: 'var(--font-size-sm)', marginBottom: 'var(--space-3)' }}>
            {logError}
          </p>
        )}

        <button
          type="button"
          onClick={handleLogAsPrint}
          disabled={createPrint.isPending || createBuild.isPending}
          style={{
            width: '100%',
            padding: 'var(--space-3)',
            background: 'var(--color-success)',
            color: 'white',
            borderRadius: 'var(--radius-md)',
            fontWeight: 600,
          }}
        >
          {createPrint.isPending || createBuild.isPending ? 'Logging…' : 'Log as print'}
        </button>
      </BottomSheet>

      <BottomSheet
        open={scenarioPickerOpen}
        onClose={() => setScenarioPickerOpen(false)}
        title="Saved scenarios"
      >
        {scenarioError && (
          <p style={{ color: 'var(--color-danger)', fontSize: 'var(--font-size-sm)', marginBottom: 'var(--space-3)' }}>
            {scenarioError}
          </p>
        )}
        {(scenarios ?? []).length === 0 ? (
          <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}>
            No saved scenarios yet. Save one from any design row using &apos;💾 Save scenario&apos;.
          </p>
        ) : (
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {(scenarios ?? []).map((s) => (
              <li key={s.id} style={{ marginBottom: 'var(--space-2)' }}>
                <div
                  style={{
                    display: 'flex',
                    gap: 'var(--space-2)',
                    background: 'var(--color-surface-2)',
                    border: '1px solid var(--color-border)',
                    borderRadius: 'var(--radius-md)',
                    padding: 'var(--space-3)',
                  }}
                >
                  <button
                    type="button"
                    onClick={() => loadScenarioIntoRow(s.id)}
                    style={{ flex: 1, textAlign: 'left', color: 'var(--color-text)', background: 'transparent' }}
                  >
                    <div style={{ fontWeight: 600, fontSize: 'var(--font-size-sm)' }}>{s.name}</div>
                    <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                      {s.print_time_hrs}h {s.print_time_mins}m · {s.units_per_plate}/plate · ${fmt2(Number(s.sale_price_per_unit))} listed
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => deleteScenario(s.id, s.name)}
                    style={{ color: 'var(--color-danger)', fontSize: 'var(--font-size-xs)' }}
                  >
                    Delete
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </BottomSheet>

      <BottomSheet
        open={loadFromProductOpen}
        onClose={() => setLoadFromProductOpen(false)}
        title="Load from product"
      >
        {loadError && (
          <p style={{ color: 'var(--color-danger)', fontSize: 'var(--font-size-sm)', marginBottom: 'var(--space-3)' }}>
            {loadError}
          </p>
        )}
        {(products ?? []).length === 0 ? (
          <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}>
            No products yet — create one first (Prints → Products → New).
          </p>
        ) : (
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {(products ?? []).map((p) => (
              <li key={p.id} style={{ marginBottom: 'var(--space-2)' }}>
                <button
                  type="button"
                  onClick={() => loadFromProduct(p.id)}
                  style={{
                    width: '100%',
                    textAlign: 'left',
                    padding: 'var(--space-3)',
                    background: 'var(--color-surface-2)',
                    border: '1px solid var(--color-border)',
                    borderRadius: 'var(--radius-md)',
                    color: 'var(--color-text)',
                    cursor: 'pointer',
                  }}
                >
                  <div style={{ fontWeight: 600, fontSize: 'var(--font-size-sm)' }}>{p.name}</div>
                  <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                    {p.default_unit_price != null
                      ? `$${fmt2(Number(p.default_unit_price))} listed`
                      : 'no listed price'}
                    {p.typical_print_time_hours != null && ` · ${p.typical_print_time_hours}h typical`}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </BottomSheet>

      <BottomSheet
        open={hardwareLibraryRowId !== null}
        onClose={() => setHardwareLibraryRowId(null)}
        title="Pick from library"
      >
        {(hardwarePresets ?? []).length === 0 ? (
          <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)', padding: 'var(--space-3) 0' }}>
            No saved items yet. Add some in Settings → Hardware library.
          </p>
        ) : (
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {(hardwarePresets ?? []).map((preset) => (
              <li key={preset.id} style={{ marginBottom: 'var(--space-2)' }}>
                <button
                  type="button"
                  onClick={() => hardwareLibraryRowId && addHardwareFromPreset(hardwareLibraryRowId, preset.id)}
                  style={{
                    width: '100%',
                    textAlign: 'left',
                    padding: 'var(--space-3)',
                    background: 'var(--color-surface-2)',
                    border: '1px solid var(--color-border)',
                    borderRadius: 'var(--radius-md)',
                    color: 'var(--color-text)',
                    cursor: 'pointer',
                  }}
                >
                  <div style={{ fontWeight: 600, fontSize: 'var(--font-size-sm)' }}>{preset.name}</div>
                  <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                    {preset.default_quantity} × ${fmt2(Number(preset.unit_cost))} = ${fmt2(Number(preset.default_quantity) * Number(preset.unit_cost))}/unit
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </BottomSheet>

      <BottomSheet
        open={saveSheetRowId !== null}
        onClose={() => setSaveSheetRowId(null)}
        title="Save to product"
      >
        <div style={{ display: 'flex', gap: 'var(--space-2)', marginBottom: 'var(--space-4)' }}>
          <button
            type="button"
            onClick={() => setSaveMode('pick')}
            style={tabStyle(saveMode === 'pick')}
          >
            Existing product
          </button>
          <button
            type="button"
            onClick={() => setSaveMode('create')}
            style={tabStyle(saveMode === 'create')}
          >
            New product
          </button>
        </div>

        {saveMode === 'pick' ? (
          products && products.length > 0 ? (
            <select
              value={saveTargetProductId}
              onChange={(e) => setSaveTargetProductId(e.target.value)}
              style={{ ...selectStyle, marginBottom: 'var(--space-4)' }}
            >
              {products.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          ) : (
            <p
              style={{
                color: 'var(--color-text-muted)',
                fontSize: 'var(--font-size-sm)',
                marginBottom: 'var(--space-4)',
              }}
            >
              No products yet. Switch to &quot;New product.&quot;
            </p>
          )
        ) : (
          <input
            type="text"
            placeholder="Product name"
            value={saveNewName}
            onChange={(e) => setSaveNewName(e.target.value)}
            style={{ ...inputStyle, marginBottom: 'var(--space-4)' }}
          />
        )}

        {saveError && (
          <p
            style={{
              color: 'var(--color-danger)',
              fontSize: 'var(--font-size-sm)',
              marginBottom: 'var(--space-3)',
            }}
          >
            {saveError}
          </p>
        )}

        <button
          type="button"
          onClick={handleSaveToProduct}
          disabled={createProduct.isPending || updateProduct.isPending}
          style={{
            width: '100%',
            padding: 'var(--space-3)',
            background: 'var(--color-primary)',
            color: 'white',
            borderRadius: 'var(--radius-md)',
            fontWeight: 600,
          }}
        >
          {createProduct.isPending || updateProduct.isPending ? 'Saving…' : 'Save'}
        </button>
      </BottomSheet>
    </div>
  )
}

// ── DesignRow ────────────────────────────────────────────────────────────────

interface DesignRowProps {
  row: RowDraft
  result: PlateScenarioResult | null
  spools: FilamentSpool[]
  defaultMomsCut: number
  hasPresets: boolean
  savingScenario: boolean
  onUpdate: (patch: Partial<RowDraft>) => void
  onUpdateSlot: (idx: number, patch: Partial<SlotDraft>) => void
  onAddHardware: () => void
  onAddHardwareFromLibrary: () => void
  onUpdateHardware: (uid: string, patch: Partial<HardwareItemDraft>) => void
  onRemoveHardware: (uid: string) => void
  onRemove: () => void
  onSave: () => void
  onSaveScenario: () => void
  onLogAsPrint: () => void
  canRemove: boolean
}

function DesignRow({
  row,
  result,
  spools,
  defaultMomsCut,
  hasPresets,
  savingScenario,
  onUpdate,
  onUpdateSlot,
  onAddHardware,
  onAddHardwareFromLibrary,
  onUpdateHardware,
  onRemoveHardware,
  onRemove,
  onSave,
  onSaveScenario,
  onLogAsPrint,
  canRemove,
}: DesignRowProps) {
  const profitColor =
    result == null
      ? 'var(--color-text-muted)'
      : result.netProfitPerUnit > 0
        ? 'var(--color-success)'
        : 'var(--color-danger)'

  const pickerSpools = spools.filter((s) => s.status === 'in-use' || s.status === 'finished')

  return (
    <Card>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontWeight: 600,
              fontSize: 'var(--font-size-sm)',
              color: 'var(--color-text)',
              marginBottom: 'var(--space-1)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {row.name || '(unnamed design)'}
          </div>
          {result != null ? (
            <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
              <span style={{ color: profitColor, fontWeight: 700 }}>
                ${fmt2(result.netProfitPerUnit)}/unit
              </span>{' '}
              · ${fmt2(result.profitPerPlate)}/plate · ${fmt2(result.profitPerHour)}/hr ·{' '}
              {fmtPct(result.marginPct)} margin
              <div style={{ marginTop: 2 }}>
                cost ${fmt2(result.costPerUnit)} · cut ${fmt2(result.momsCutPerUnit)} (
                {result.momsCutPct}%)
              </div>
            </div>
          ) : (
            <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-faint)' }}>
              Fill in hours + units to see results
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={() => onUpdate({ expanded: !row.expanded })}
          style={{
            marginLeft: 'var(--space-3)',
            color: 'var(--color-text-muted)',
            fontSize: 'var(--font-size-sm)',
            flexShrink: 0,
          }}
          aria-label={row.expanded ? 'Collapse row' : 'Expand row'}
        >
          {row.expanded ? '▲' : '▼'}
        </button>
      </div>

      {row.expanded && (
        <div style={{ marginTop: 'var(--space-4)' }}>
          <div style={{ marginBottom: 'var(--space-3)' }}>
            <label style={labelStyleBlock}>Design name</label>
            <input
              type="text"
              placeholder="e.g., Articulated Dragon"
              value={row.name}
              onChange={(e) => onUpdate({ name: e.target.value })}
              style={inputStyle}
            />
          </div>

          <div style={{ marginBottom: 'var(--space-3)' }}>
            <label style={labelStyleBlock}>Model link (optional)</label>
            <input
              type="url"
              placeholder="https://makerworld.com/… or printables.com/…"
              value={row.modelLink}
              onChange={(e) => onUpdate({ modelLink: e.target.value })}
              style={inputStyle}
            />
            <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-faint)', marginTop: 4 }}>
              Saved to the product&apos;s &quot;model source&quot; when you save.
            </p>
          </div>

          <div style={{ marginBottom: 'var(--space-3)' }}>
            <label style={labelStyleBlock}>Filament slots (up to 4 colors)</label>
            {row.slots.map((slot, idx) => {
              const total = slotTotalGrams(slot)
              const disabled = !slot.spoolId
              return (
                <div
                  key={idx}
                  style={{
                    background: 'var(--color-surface-2)',
                    padding: 'var(--space-2)',
                    borderRadius: 'var(--radius-md)',
                    marginBottom: 'var(--space-2)',
                  }}
                >
                  <SpoolPicker
                    spools={pickerSpools}
                    value={slot.spoolId}
                    onChange={(id) => onUpdateSlot(idx, { spoolId: id })}
                    ariaLabel={`Slot ${idx + 1} spool`}
                  />
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr 1fr',
                      gap: 'var(--space-2)',
                      alignItems: 'end',
                    }}
                  >
                    <GramsField
                      label="Model g"
                      value={slot.modelGrams}
                      onChange={(v) => onUpdateSlot(idx, { modelGrams: v })}
                      disabled={disabled}
                      ariaLabel={`Slot ${idx + 1} model grams`}
                    />
                    <GramsField
                      label="Supports g"
                      value={slot.supportGrams}
                      onChange={(v) => onUpdateSlot(idx, { supportGrams: v })}
                      disabled={disabled}
                      ariaLabel={`Slot ${idx + 1} support grams`}
                    />
                    <GramsField
                      label="Purge g"
                      value={slot.purgeGrams}
                      onChange={(v) => onUpdateSlot(idx, { purgeGrams: v })}
                      disabled={disabled}
                      ariaLabel={`Slot ${idx + 1} purge grams`}
                    />
                  </div>
                  {!disabled && (
                    <div
                      style={{
                        textAlign: 'right',
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--color-text-muted)',
                        marginTop: 4,
                      }}
                    >
                      Total: <strong>{total}g</strong>
                    </div>
                  )}
                </div>
              )
            })}
            <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-faint)', marginTop: 4 }}>
              Bambu Studio shows each amount separately on the slice — enter them as-is.
            </p>
          </div>

          <div style={{ marginBottom: 'var(--space-3)' }}>
            <label style={labelStyleBlock}>Print time</label>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 'var(--space-2)',
                alignItems: 'center',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                <input
                  type="text"
                  inputMode="numeric"
                  placeholder="0"
                  value={row.printTimeHrs}
                  onChange={(e) => onUpdate({ printTimeHrs: e.target.value })}
                  style={inputStyle}
                  data-testid="input-print-time-hrs"
                  aria-label="Print time hours"
                />
                <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)' }}>
                  hrs
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                <input
                  type="text"
                  inputMode="numeric"
                  placeholder="0"
                  value={row.printTimeMins}
                  onChange={(e) => onUpdate({ printTimeMins: e.target.value })}
                  style={inputStyle}
                  data-testid="input-print-time-mins"
                  aria-label="Print time minutes"
                />
                <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)' }}>
                  mins
                </span>
              </div>
            </div>
          </div>

          <div style={{ marginBottom: 'var(--space-3)' }}>
            <label style={labelStyleBlock}>Units / plate</label>
            <input
              type="text"
              inputMode="numeric"
              placeholder="e.g., 12"
              value={row.unitsPerPlate}
              onChange={(e) => onUpdate({ unitsPerPlate: e.target.value })}
              style={inputStyle}
              data-testid="input-units"
            />
          </div>
          <div style={{ marginBottom: 'var(--space-3)' }}>
            <label style={labelStyleBlock}>Hardware per unit (optional)</label>
            {row.hardwareItems.length === 0 ? (
              <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-faint)', marginBottom: 4 }}>
                Add things you attach to each print — keyring, magnet, key cap, etc.
              </p>
            ) : (
              row.hardwareItems.map((item) => (
                <div
                  key={item.uid}
                  style={{
                    display: 'flex',
                    gap: 'var(--space-2)',
                    marginBottom: 'var(--space-2)',
                    alignItems: 'center',
                  }}
                >
                  <input
                    type="text"
                    placeholder="e.g., Keyring"
                    value={item.name}
                    onChange={(e) => onUpdateHardware(item.uid, { name: e.target.value })}
                    style={{ ...inputStyle, flex: 2 }}
                    aria-label="Hardware name"
                  />
                  <input
                    type="text"
                    inputMode="decimal"
                    placeholder="qty"
                    value={item.quantity}
                    onChange={(e) => onUpdateHardware(item.uid, { quantity: e.target.value })}
                    style={{ ...inputStyle, width: 56 }}
                    aria-label="Hardware quantity per unit"
                  />
                  <input
                    type="text"
                    inputMode="decimal"
                    placeholder="$ each"
                    value={item.unitCost}
                    onChange={(e) => onUpdateHardware(item.uid, { unitCost: e.target.value })}
                    style={{ ...inputStyle, width: 76 }}
                    aria-label="Hardware unit cost"
                  />
                  <button
                    type="button"
                    onClick={() => onRemoveHardware(item.uid)}
                    style={{
                      color: 'var(--color-text-muted)',
                      padding: '0 var(--space-2)',
                      fontSize: 'var(--font-size-lg)',
                      lineHeight: 1,
                    }}
                    aria-label="Remove hardware item"
                  >
                    ×
                  </button>
                </div>
              ))
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4, gap: 'var(--space-2)', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
                <button
                  type="button"
                  onClick={onAddHardware}
                  style={{
                    color: 'var(--color-primary)',
                    fontSize: 'var(--font-size-xs)',
                    fontWeight: 600,
                  }}
                >
                  + Add custom
                </button>
                {hasPresets && (
                  <button
                    type="button"
                    onClick={onAddHardwareFromLibrary}
                    style={{
                      color: 'var(--color-primary)',
                      fontSize: 'var(--font-size-xs)',
                      fontWeight: 600,
                    }}
                  >
                    + From library
                  </button>
                )}
              </div>
              {row.hardwareItems.length > 0 && (
                <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                  Total: <strong>${fmt2(sumHardwareCostPerUnit(row.hardwareItems))}</strong>/unit
                </span>
              )}
            </div>
          </div>

          <div style={{ marginBottom: 'var(--space-3)' }}>
            <label style={labelStyleBlock}>Handling min / unit</label>
            <input
              type="text"
              inputMode="decimal"
              placeholder="0"
              value={row.handlingMinutesPerUnit}
              onChange={(e) => onUpdate({ handlingMinutesPerUnit: e.target.value })}
              style={inputStyle}
            />
          </div>
          <Pair
            left={{
              label: 'Sale price/unit ($)',
              value: row.salePricePerUnit,
              onChange: (v) => onUpdate({ salePricePerUnit: v }),
              placeholder: 'e.g., 2.00',
              testId: 'input-sale-price',
            }}
            right={{
              label: "Mom's cut % (override)",
              value: row.momsCutPctOverride,
              onChange: (v) => onUpdate({ momsCutPctOverride: v }),
              placeholder: `default ${defaultMomsCut}%`,
            }}
          />

          {result != null && (
            <>
              <div
                style={{
                  background: 'var(--color-surface-2)',
                  borderRadius: 'var(--radius-md)',
                  padding: 'var(--space-3)',
                  fontSize: 'var(--font-size-xs)',
                  marginBottom: 'var(--space-3)',
                  marginTop: 'var(--space-4)',
                }}
              >
                <div
                  style={{
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--color-text-faint)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    fontWeight: 700,
                    marginBottom: 'var(--space-2)',
                  }}
                >
                  Per plate
                </div>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr auto',
                    gap: 'var(--space-1) var(--space-3)',
                  }}
                >
                  <BreakdownRow label="Filament" value={`$${fmt2(result.materialCost)}`} />
                  <BreakdownRow
                    label="Power + wear"
                    value={`$${fmt2(result.electricityCost + result.machineWearCost)}`}
                  />
                  <BreakdownRow label="Hardware" value={`$${fmt2(result.hardwareCost)}`} />
                  <BreakdownRow label="Labor" value={`$${fmt2(result.laborCost)}`} />
                  <BreakdownRow
                    label="Failure buffer"
                    value={`+$${fmt2(
                      result.bufferedVariable -
                        result.materialCost -
                        result.electricityCost -
                        result.machineWearCost,
                    )}`}
                    valueColor="var(--color-warning-text)"
                  />
                  <Divider />
                  <BreakdownRow
                    label="Total cost / plate"
                    value={`$${fmt2(result.totalPlateCost)}`}
                    bold
                  />
                  <BreakdownRow
                    label={`Mom's cut / plate (${result.momsCutPct}%)`}
                    value={`$${fmt2(result.momsCutPerUnit * Number(row.unitsPerPlate || 0))}`}
                  />
                </div>

                <div
                  style={{
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--color-text-faint)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    fontWeight: 700,
                    marginTop: 'var(--space-3)',
                    marginBottom: 'var(--space-2)',
                  }}
                >
                  Per unit
                </div>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr auto',
                    gap: 'var(--space-1) var(--space-3)',
                  }}
                >
                  <BreakdownRow label="Cost / unit" value={`$${fmt2(result.costPerUnit)}`} />
                  <BreakdownRow
                    label={`Mom's cut / unit (${result.momsCutPct}%)`}
                    value={`$${fmt2(result.momsCutPerUnit)}`}
                  />
                  <Divider />
                  <BreakdownRow
                    label="Net profit / unit"
                    value={`$${fmt2(result.netProfitPerUnit)}`}
                    bold
                    labelColor={profitColor}
                    valueColor={profitColor}
                  />
                  <BreakdownRow
                    label="Break-even price / unit"
                    value={
                      result.breakEvenPerUnit === Infinity
                        ? '∞'
                        : `$${fmt2(result.breakEvenPerUnit)}`
                    }
                  />
                </div>
              </div>

              <div style={{ marginBottom: 'var(--space-3)' }} data-testid="profit-chip">
                <Chip tone={result.netProfitPerUnit > 0 ? 'success' : 'danger'}>
                  {result.netProfitPerUnit > 0
                    ? `Profitable — ${fmtPct(result.marginPct)} margin`
                    : `Unprofitable — raise price to $${fmt2(
                        result.breakEvenPerUnit === Infinity ? 0 : result.breakEvenPerUnit,
                      )}`}
                </Chip>
              </div>
            </>
          )}

          <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={onSave}
              style={{
                padding: 'var(--space-2) var(--space-4)',
                background: 'var(--color-primary)',
                color: 'white',
                borderRadius: 'var(--radius-md)',
                fontWeight: 600,
                fontSize: 'var(--font-size-sm)',
              }}
              data-testid="save-to-product-btn"
            >
              Save to product
            </button>
            <button
              type="button"
              onClick={onSaveScenario}
              disabled={savingScenario}
              style={{
                padding: 'var(--space-2) var(--space-4)',
                color: 'var(--color-primary)',
                border: '1px solid var(--color-primary)',
                borderRadius: 'var(--radius-md)',
                fontWeight: 600,
                fontSize: 'var(--font-size-sm)',
                background: 'transparent',
              }}
              title={row.scenarioId ? 'Update saved scenario' : 'Save as scenario'}
            >
              {savingScenario ? 'Saving…' : row.scenarioId ? '💾 Update scenario' : '💾 Save scenario'}
            </button>
            <button
              type="button"
              onClick={onLogAsPrint}
              disabled={!row.productId}
              title={
                row.productId
                  ? 'Convert this design into an actual print transaction + add units to inventory'
                  : 'Save to a product first so we know which inventory to update'
              }
              style={{
                padding: 'var(--space-2) var(--space-4)',
                color: row.productId ? 'var(--color-success-text)' : 'var(--color-text-faint)',
                border: `1px solid ${row.productId ? 'var(--color-success)' : 'var(--color-border)'}`,
                borderRadius: 'var(--radius-md)',
                fontWeight: 600,
                fontSize: 'var(--font-size-sm)',
                background: 'transparent',
                cursor: row.productId ? 'pointer' : 'not-allowed',
              }}
            >
              🖨️ Log as print
            </button>
            {canRemove && (
              <button
                type="button"
                onClick={onRemove}
                style={{
                  padding: 'var(--space-2) var(--space-4)',
                  color: 'var(--color-danger)',
                  border: '1px solid var(--color-danger)',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--font-size-sm)',
                }}
                aria-label="Remove design row"
              >
                Remove
              </button>
            )}
          </div>
        </div>
      )}
    </Card>
  )
}

// ── Small helpers ────────────────────────────────────────────────────────────

interface PairField {
  label: string
  value: string
  onChange: (v: string) => void
  placeholder: string
  testId?: string
}
function Pair({ left, right }: { left: PairField; right: PairField }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 'var(--space-3)',
        marginBottom: 'var(--space-3)',
      }}
    >
      <FieldSlot {...left} />
      <FieldSlot {...right} />
    </div>
  )
}
function SpoolPicker({
  spools,
  value,
  onChange,
  ariaLabel,
}: {
  spools: FilamentSpool[]
  value: string
  onChange: (id: string) => void
  ariaLabel: string
}) {
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)

  const selected = spools.find((s) => s.id === value)

  const spoolLabel = (s: FilamentSpool): string => {
    const parts: string[] = []
    if (s.brand) parts.push(s.brand)
    parts.push(s.color)
    parts.push(s.material)
    const costPerKg =
      s.purchase_price != null && s.original_weight_grams > 0
        ? (Number(s.purchase_price) / Number(s.original_weight_grams)) * 1000
        : null
    let label = parts.join(' ')
    if (costPerKg != null) label += ` ($${costPerKg.toFixed(2)}/kg)`
    return label
  }

  const matches = spools.filter((s) => {
    if (!query.trim()) return true
    const haystack = `${s.brand ?? ''} ${s.color} ${s.material}`.toLowerCase()
    return haystack.includes(query.trim().toLowerCase())
  })

  const displayText = open ? query : selected ? spoolLabel(selected) : ''

  return (
    <div style={{ position: 'relative', marginBottom: 'var(--space-2)' }}>
      <input
        type="text"
        value={displayText}
        onChange={(e) => {
          setQuery(e.target.value)
          if (!open) setOpen(true)
        }}
        onFocus={() => {
          setQuery('')
          setOpen(true)
        }}
        // Defer blur so onMouseDown on the dropdown row can fire first.
        onBlur={() => window.setTimeout(() => setOpen(false), 120)}
        placeholder={selected ? '' : 'Search spools by brand, color, material…'}
        style={inputStyle}
        aria-label={ariaLabel}
      />
      {open && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 2px)',
            left: 0,
            right: 0,
            background: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-md)',
            maxHeight: 220,
            overflowY: 'auto',
            zIndex: 20,
            boxShadow: 'var(--shadow-md)',
          }}
        >
          <div
            onMouseDown={(e) => {
              e.preventDefault()
              onChange('')
              setQuery('')
              setOpen(false)
            }}
            style={{
              padding: 'var(--space-2) var(--space-3)',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--color-text-faint)',
              cursor: 'pointer',
              borderBottom: '1px solid var(--color-border)',
            }}
          >
            — empty —
          </div>
          {matches.length === 0 ? (
            <div
              style={{
                padding: 'var(--space-3)',
                fontSize: 'var(--font-size-sm)',
                color: 'var(--color-text-muted)',
                textAlign: 'center',
              }}
            >
              No spools match &quot;{query}&quot;
            </div>
          ) : (
            matches.map((s) => (
              <div
                key={s.id}
                onMouseDown={(e) => {
                  e.preventDefault()
                  onChange(s.id)
                  setQuery('')
                  setOpen(false)
                }}
                style={{
                  padding: 'var(--space-2) var(--space-3)',
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--color-text)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-2)',
                  background: s.id === value ? 'var(--color-surface-2)' : 'transparent',
                }}
              >
                {s.color_hex && (
                  <span
                    style={{
                      display: 'inline-block',
                      width: 14,
                      height: 14,
                      borderRadius: '50%',
                      background: s.color_hex,
                      border: '1px solid var(--color-border-strong)',
                      flexShrink: 0,
                    }}
                  />
                )}
                <span>{spoolLabel(s)}</span>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  )
}

function GramsField({
  label,
  value,
  onChange,
  disabled,
  ariaLabel,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  disabled: boolean
  ariaLabel: string
}) {
  return (
    <div>
      <label style={labelStyleBlock}>{label}</label>
      <input
        type="text"
        inputMode="decimal"
        placeholder="0"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={inputStyle}
        aria-label={ariaLabel}
        disabled={disabled}
      />
    </div>
  )
}

function FieldSlot({ label, value, onChange, placeholder, testId }: PairField) {
  return (
    <div>
      <label style={labelStyleBlock}>{label}</label>
      <input
        type="text"
        inputMode="decimal"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={inputStyle}
        {...(testId ? { 'data-testid': testId } : {})}
      />
    </div>
  )
}
function BreakdownRow({
  label,
  value,
  bold,
  labelColor,
  valueColor,
}: {
  label: string
  value: string
  bold?: boolean
  labelColor?: string
  valueColor?: string
}) {
  return (
    <>
      <span style={{ color: labelColor ?? 'var(--color-text-muted)', fontWeight: bold ? 700 : 400 }}>
        {label}
      </span>
      <span style={{ textAlign: 'right', color: valueColor, fontWeight: bold ? 700 : 400 }}>
        {value}
      </span>
    </>
  )
}
function Divider() {
  return (
    <span
      style={{
        gridColumn: '1/-1',
        borderTop: '1px solid var(--color-border)',
        margin: '4px 0',
      }}
    />
  )
}

// ── Shared styles ────────────────────────────────────────────────────────────

const inputStyle: CSSProperties = {
  padding: 'var(--space-2) var(--space-3)',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--color-border)',
  background: 'var(--color-bg)',
  color: 'var(--color-text)',
  fontSize: 'var(--font-size-sm)',
  width: '100%',
}
const selectStyle: CSSProperties = {
  ...inputStyle,
  padding: 'var(--space-3)',
  background: 'var(--color-surface)',
}
const labelStyleBlock: CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-text-muted)',
  fontWeight: 600,
  display: 'block',
  marginBottom: 2,
}
const pillStyle = (active: boolean, tone: 'primary' | 'warning'): CSSProperties => {
  const accentBorder = tone === 'primary' ? 'var(--color-primary)' : 'var(--color-warning)'
  const accentBg = tone === 'primary' ? 'var(--color-primary-soft)' : 'var(--color-warning-soft)'
  const accentFg = tone === 'primary' ? 'var(--color-primary-text)' : 'var(--color-warning-text)'
  return {
    padding: 'var(--space-1) var(--space-3)',
    borderRadius: 'var(--radius-pill)',
    border: `1px solid ${active ? accentBorder : 'var(--color-border)'}`,
    background: active ? accentBg : 'transparent',
    color: active ? accentFg : 'var(--color-text-muted)',
    fontSize: 'var(--font-size-xs)',
    fontWeight: active ? 700 : 400,
    cursor: 'pointer',
  }
}
const tabStyle = (active: boolean): CSSProperties => ({
  flex: 1,
  padding: 'var(--space-2)',
  borderRadius: 'var(--radius-md)',
  border: `1px solid ${active ? 'var(--color-primary)' : 'var(--color-border)'}`,
  background: active ? 'var(--color-primary-soft)' : 'transparent',
  color: active ? 'var(--color-primary-text)' : 'var(--color-text-muted)',
  fontWeight: 600,
  fontSize: 'var(--font-size-sm)',
  cursor: 'pointer',
})
